import type { AxiosError } from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";

import type {
  Moderator,
  PauseModeratorAccountHeadRequest,
  PauseModeratorAccountRequest,
} from "@/entities/moderator";
import { apiInstance } from "@/shared/api";
import { filterObjectKeys } from "@/shared/lib/utils";
import type {
  PaginationRequest,
  PaginatedResponse,
  ApiResponse,
} from "@/shared/types";

export const useGetModerators = (
  props: PaginationRequest & { isBanned?: boolean; isMaster?: boolean },
) => {
  const params = filterObjectKeys(props);

  return useQuery<PaginatedResponse<Moderator>>({
    queryKey: ["moderators", params],
    queryFn: async () => {
      const { data } = await apiInstance.get<PaginatedResponse<Moderator>>(
        "/moderators",
        params,
      );

      return data;
    },
  });
};

export const useGetModeratorById = ({ userId }: { userId: string }) => {
  return useQuery<ApiResponse<Moderator>>({
    queryKey: ["moderator", userId],
    queryFn: async () => {
      const { data } = await apiInstance.get<ApiResponse<Moderator>>(
        `/moderators/user/${userId}`,
      );

      return data;
    },
  });
};

export const usePauseModeratorAccount = () => {
  return useMutation<void, AxiosError, PauseModeratorAccountRequest>({
    mutationKey: ["pauseModerator"],
    mutationFn: async (data) => {
      await apiInstance.patch("/moderators/pause", data);
    },
  });
};

export const usePauseModeratorAccountHead = () => {
  return useMutation<void, AxiosError, PauseModeratorAccountHeadRequest>({
    mutationKey: ["pauseModeratorHead"],
    mutationFn: async ({ pauseStatus, id }) => {
      await apiInstance.patch(`/moderators/${id}`, { pauseStatus });
    },
  });
};
